// Canal « Composite RGB » : trois fluorescences de la même grille de pixels affichées en rouge, vert et bleu, chacune
// avec le contraste de sa carte (et son flou) ; par couleur, un gain resserre le haut (haut' = bas + (haut − bas) /
// gain) et le bas est le niveau du fond, mis à 0 (estimé sur l'image : médiane des pixels sous le seuil d'Otsu du plus
// petit niveau, ou réglé) : fenêtre de la couleur (compositeWindows). L'inversion des cartes n'est pas reprise
// (composite sur fond noir).
// Différences accentuées (décorrélation, « decorrelation stretch » de l'imagerie multispectrale) : les trois
// intensités x (dans [0, 1]) sont très corrélées (même tissu), d'où un composite grisâtre. Sur le tissu (somme au-dessus
// du seuil d'Otsu), moyenne μ et covariance C = V Λ Vᵀ ; transformation y = μc + k V Λ^(−1/2) Vᵀ (x − μ) : composantes
// décorrélées ramenées au même écart-type k (« contraste ») autour du gris moyen μc, repassées dans les axes d'origine
// (le rouge reste à peu près le canal rouge). Les composantes faibles (bruit, plus fort en pleine résolution que sur le
// niveau des statistiques) ne sont pas amplifiées plus de 5 fois par rapport à la principale. Force s : y = (1 − s) x
// + s (transformation) ; extrêmes adoucis (0,5 + 0,5 tanh(2 (y − 0,5))) plutôt qu'écrêtés. Sans tissu : noir.
// Saturation (direct ou décorrélé) : y = l + saturation × (y − l), l = luminance de y. Point noir de l'image (direct ou
// décorrélé) : y = (y − noir) / (1 − noir), en dessous 0 (le gris du tissu faible et du fond décorrélés passe au noir).
import { VivLayerExtension } from '@hms-dbmi/viv';

import { otsu } from './blood';
import { excitationOf } from './redox';
import { pixelSize } from '../../services';

export const COMPOSITE_ID = 'composite';  // identifiant du canal parmi les modalités
// r, g, b : ids des images de chaque couleur (null : affectation par défaut) ; gainR, gainG, gainB : intensités ;
// blackR, blackG, blackB : niveau mis à 0, dans les unités lues (null : fond estimé) ;
// display : 'direct' ou 'decorrelated' (différences accentuées) ; strength : force de la décorrélation (0 à 1) ;
// contrast : écart-type visé des composantes décorrélées ; saturation : gain des couleurs autour de la luminance ;
// smooth : lissage des trois images avant le mélange (σ en px de chaque niveau, ≈ px d'écran : bruit coloré) ;
// threshold : seuil du fond en décorrélé (somme des trois intensités, de 0 à 3 ; null : Otsu) ; black : point noir de
// l'image (0 à 0,9).
export const COMPOSITE_DEFAULTS = {
  r: null, g: null, b: null, gainR: 1, gainG: 1, gainB: 1, blackR: null, blackG: null, blackB: null, display: 'direct',
  strength: 1,
  contrast: 0.1, saturation: 1,
  smooth: 1, threshold: null, black: 0,
};
const TARGET_MEAN = 0.5; // gris moyen du tissu
const MAX_BOOST = 5;     // amplification maximale d'une composante par rapport à la principale
export const COMPOSITE_KEYS = ['r', 'g', 'b'];
export const COMPOSITE_COLORS = { r: [255, 0, 0], g: [0, 255, 0], b: [0, 0, 255] };
export const COMPOSITE_GAINS = { r: 'gainR', g: 'gainG', b: 'gainB' };
export const COMPOSITE_BLACKS = { r: 'blackR', g: 'blackG', b: 'blackB' };

// Niveau du fond d'une image (unités lues) : médiane des pixels sous le seuil d'Otsu de son plus petit niveau (verre,
// noir de la caméra).
export async function backgroundLevel(ch) {
  const { data } = await ch.loader[ch.loader.length - 1].getRaster({ selection: { t: 0, z: 0, c: 0 } });
  const t = otsu(data);
  const below = Float32Array.from(data.filter(v => v <= t)).sort();
  return below.length ? below[Math.floor(below.length / 2)] : 0;
}

// Niveau mis à 0 de chaque couleur : celui réglé, sinon le fond estimé (pas sous le bas du contraste de la carte).
export const blackLevels = (chans, settings, backgrounds) => chans.map((ch, i) => (
  settings[COMPOSITE_BLACKS[COMPOSITE_KEYS[i]]] ?? Math.max(ch.contrast[0], backgrounds[i] ?? ch.contrast[0])));

// Fenêtre [bas, haut] de chaque couleur, dans les unités lues : bas = niveau mis à 0, haut = contraste de la carte
// resserré par le gain (toujours au-dessus du bas).
export function compositeWindows(chans, settings, backgrounds = []) {
  const blacks = blackLevels(chans, settings, backgrounds);
  return chans.map((ch, i) => {
    const [lo, hi] = ch.contrast;
    const high = lo + (hi - lo) / (settings[COMPOSITE_GAINS[COMPOSITE_KEYS[i]]] || 1);
    return [blacks[i], Math.max(high, blacks[i] + 0.01 * ((hi - lo) || 1))];
  });
}

// Gains équilibrés : pour chaque image, le 99,5e centile de son plus petit niveau ramené au haut du contraste (gain
// entre 0,25 et 4). contrast : contraste de sa carte, dans les unités lues.
export async function balanceGains(chans) {
  return Promise.all(chans.map(async ch => {
    const { data } = await ch.loader[ch.loader.length - 1].getRaster({ selection: { t: 0, z: 0, c: 0 } });
    const sorted = Float32Array.from(data).sort();
    const top = sorted[Math.floor(sorted.length * 0.995)];
    const [lo, hi] = ch.contrast;
    return Math.round(Math.min(4, Math.max(0.25, (hi - lo) / Math.max(1e-6, top - lo))) * 100) / 100;
  }));
}

// Décomposition d'une matrice symétrique 3 × 3 (Jacobi) : { values, vectors (colonnes) }.
function eigenSym3(A) {
  const a = A.map(row => row.slice());
  const V = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep += 1) {
    if (a[0][1] ** 2 + a[0][2] ** 2 + a[1][2] ** 2 < 1e-24) break;
    for (const [p, q] of [[0, 1], [0, 2], [1, 2]]) {
      if (Math.abs(a[p][q]) < 1e-18) continue;
      const theta = (a[q][q] - a[p][p]) / (2 * a[p][q]);
      const t = (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const c = 1 / Math.sqrt(t * t + 1);
      const s = t * c;
      for (let k = 0; k < 3; k += 1) {
        const [akp, akq] = [a[k][p], a[k][q]];
        a[k][p] = c * akp - s * akq; a[k][q] = s * akp + c * akq;
      }
      for (let k = 0; k < 3; k += 1) {
        const [apk, aqk] = [a[p][k], a[q][k]];
        a[p][k] = c * apk - s * aqk; a[q][k] = s * apk + c * aqk;
      }
      for (let k = 0; k < 3; k += 1) {
        const [vkp, vkq] = [V[k][p], V[k][q]];
        V[k][p] = c * vkp - s * vkq; V[k][q] = s * vkp + c * vkq;
      }
    }
  }
  return { values: [a[0][0], a[1][1], a[2][2]], vectors: V };
}

// Statistiques du tissu pour la décorrélation, sur un niveau d'au moins 500 000 pixels : intensités normalisées comme
// à l'affichage (fenêtre de chaque couleur, compositeWindows), tissu = somme au-dessus du seuil (celui donné, sinon
// Otsu de la somme), moyenne μ et matrice de décorrélation W = V Λ^(−1/2) Vᵀ ; otsu (seuil automatique) et area (part
// de l'image comptée comme tissu). null si trop peu de tissu.
export async function compositeStats(chans, windows, chosenThreshold = null) {
  let level = chans[0].loader.length - 1;
  while (level > 0 && pixelSize(chans[0].loader[level]).width * pixelSize(chans[0].loader[level]).height < 5e5) level -= 1;
  const rasters = await Promise.all(chans.map(ch => ch.loader[Math.min(level, ch.loader.length - 1)]
    .getRaster({ selection: { t: 0, z: 0, c: 0 } })));
  const n = rasters[0].data.length;
  const x = chans.map((ch, i) => {
    const [lo, hi] = windows[i];
    const span = (hi - lo) || 1;
    return Float32Array.from(rasters[i].data, v => Math.min(1, Math.max(0, (v - lo) / span)));
  });
  const sum = new Float32Array(n);
  for (let j = 0; j < n; j += 1) sum[j] = x[0][j] + x[1][j] + x[2][j];
  const otsuThreshold = otsu(sum);
  const threshold = chosenThreshold ?? otsuThreshold;
  const mean = [0, 0, 0];
  const cov = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  let count = 0;
  for (let j = 0; j < n; j += 1) {
    if (sum[j] < threshold) continue;
    count += 1;
    for (let a = 0; a < 3; a += 1) mean[a] += x[a][j];
  }
  if (count < 100) return null;
  for (let a = 0; a < 3; a += 1) mean[a] /= count;
  for (let j = 0; j < n; j += 1) {
    if (sum[j] < threshold) continue;
    const d = [x[0][j] - mean[0], x[1][j] - mean[1], x[2][j] - mean[2]];
    for (let a = 0; a < 3; a += 1) for (let b = 0; b < 3; b += 1) cov[a][b] += d[a] * d[b];
  }
  for (let a = 0; a < 3; a += 1) for (let b = 0; b < 3; b += 1) cov[a][b] /= count - 1;
  const { values, vectors: V } = eigenSym3(cov);
  const top = Math.max(...values);
  const inv = values.map(l => 1 / Math.sqrt(Math.max(l, top / MAX_BOOST ** 2, 1e-12)));
  const W = [0, 1, 2].map(a => [0, 1, 2].map(b => [0, 1, 2].reduce((acc, k) => acc + V[a][k] * inv[k] * V[b][k], 0)));
  return { mean, W, threshold, otsu: otsuThreshold, area: count / n, variances: values };
}

// Transformation affichée (lignes de M, décalage b, seuil, saturation, adoucissement des extrêmes) : identité en direct,
// sinon y = (1 − s) x + s (μc + k W (x − μ)).
export function compositeTransform(stats, {
  display, strength = 1, contrast = 0.1, saturation = 1, threshold = null, black = 0,
}) {
  const s = display === 'decorrelated' && stats ? Math.min(1, Math.max(0, strength)) : 0;
  const id = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  if (!s) return { m: id, b: [0, 0, 0], threshold: 0, saturation, soft: 0, black };
  const { W, mean } = stats;
  const m = id.map((row, a) => row.map((v, c) => (1 - s) * v + s * contrast * W[a][c]));
  const b = [0, 1, 2].map(a => s * (TARGET_MEAN - contrast * W[a].reduce((acc, w, c) => acc + w * mean[c], 0)));
  return { m, b, threshold: threshold ?? stats.otsu, saturation, soft: 1, black };  // seuil réglé : suivi tout de suite
}

const compositeModule = {
  name: 'compositeModule',
  uniformTypes: {
    m0: 'vec3<f32>', m1: 'vec3<f32>', m2: 'vec3<f32>', offset: 'vec3<f32>', threshold: 'f32', saturation: 'f32', soft: 'f32',
    black: 'f32',
  },
  fs: `
uniform compositeModuleUniforms {
  vec3 m0;
  vec3 m1;
  vec3 m2;
  vec3 offset;
  float threshold;
  float saturation;
  float soft;
  float black;
} compositeModule;

vec3 composite_color(float[NUM_CHANNELS] intensity) {
  vec3 x = clamp(vec3(intensity[0], intensity[1], intensity[2]), 0., 1.);
  if (x.r + x.g + x.b < compositeModule.threshold) return vec3(0.);  // sans tissu
  vec3 y = vec3(dot(compositeModule.m0, x), dot(compositeModule.m1, x), dot(compositeModule.m2, x)) + compositeModule.offset;
  float l = dot(y, vec3(0.299, 0.587, 0.114));
  y = vec3(l) + compositeModule.saturation * (y - vec3(l));  // couleurs plus ou moins vives, même luminance
  if (compositeModule.soft > 0.5) y = 0.5 + 0.5 * tanh(2. * (y - 0.5));  // extrêmes adoucis plutôt qu'écrêtés
  y = (y - compositeModule.black) / max(1. - compositeModule.black, 0.001);  // point noir : en dessous, 0
  return clamp(y, 0., 1.);
}`,
  inject: { 'fs:DECKGL_MUTATE_COLOR': 'rgba = vec4(composite_color(intensity), 1.0);' },
};

// Transformation lue dans la prop « composite » du calque ({ m, b, threshold }) : un changement ne met à jour que les
// uniformes.
export class CompositeExtension extends VivLayerExtension {
  getVivShaderTemplates() { return { modules: [compositeModule] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const t = this.props.composite || { m: [[1, 0, 0], [0, 1, 0], [0, 0, 1]], b: [0, 0, 0], threshold: 0, saturation: 1, soft: 0 };
    const uniforms = {
      m0: t.m[0], m1: t.m[1], m2: t.m[2], offset: t.b, threshold: t.threshold, saturation: t.saturation ?? 1, soft: t.soft ?? 0,
      black: t.black ?? 0,
    };
    for (const model of this.getModels()) model.shaderInputs.setProps({ compositeModule: uniforms });
  }
}
CompositeExtension.extensionName = 'CompositeExtension';

// Recette du rendu affiché (tools/composite/composite_render.py la rejoue sur les fichiers) : images de chaque couleur,
// fenêtres (unités lues par le viewer et, si connues, unités d'origine), flous, réglages, statistiques et
// transformation appliquées. windows : compositeWindows ; transform : compositeTransform.
export function compositeRecipe({ sample, chans, windows, settings, stats, transform }) {
  const round = (v, d = 6) => (v == null ? null : Math.round(v * 10 ** d) / 10 ** d);
  return {
    format: 'hyprview-composite-recipe',
    version: 1,
    sample,
    created: new Date().toISOString(),
    channels: Object.fromEntries(COMPOSITE_KEYS.map((key, i) => {
      const ch = chans[i];
      const original = ch.toOriginal ? windows[i].map(ch.toOriginal)
        : ch.loader[0].dtype !== 'Uint8' ? windows[i] : null;
      return [key, {
        name: ch.name,
        modality: ch.modality,
        window: windows[i].map(v => round(v, 3)),  // valeurs lues par le viewer (copie 8 bits envoyée : celles du fichier)
        window_original: original && original.map(v => round(v, 3)),  // comptes d'origine (original 16 bits)
        blur_sigma_px: ch.blur || 0,  // flou de la carte, px pleine résolution
      }];
    })),
    smooth_sigma_level_px: settings.smooth ?? 1,  // lissage avant le mélange, px du niveau affiché
    display: settings.display === 'decorrelated' ? 'decorrelated' : 'direct',
    strength: settings.strength ?? 1,
    contrast: settings.contrast ?? 0.1,
    saturation: settings.saturation ?? 1,
    black_point: settings.black ?? 0,
    threshold: round(transform.threshold),  // somme des trois intensités normalisées sous laquelle : noir (décorrélé)
    threshold_mode: settings.threshold == null ? 'otsu' : 'manual',
    target_mean: TARGET_MEAN,
    max_boost: MAX_BOOST,
    stats: stats ? {
      mean: stats.mean.map(v => round(v)),
      W: stats.W.map(row => row.map(v => round(v))),
      variances: stats.variances.map(v => round(v, 9)),
      tissue_share: round(stats.area, 4),
    } : null,
    transform: { M: transform.m.map(row => row.map(v => round(v))), b: transform.b.map(v => round(v)) },
  };
}

// Images de chaque couleur, distinctes : celles choisies, sinon les fluorescences excitées aux plus grandes longueurs
// d'onde en rouge, vert puis bleu (epi470, epi405, epi375 : la plus courte en bleu), puis les autres fluorescences.
export function compositeChannels(fluo, settings) {
  const nm = ch => excitationOf(ch) || 0;
  const fallback = [...fluo].sort((x, y) => nm(y) - nm(x));
  return COMPOSITE_KEYS.reduce((acc, key) => {
    const used = acc.map(ch => ch?.id);
    const ch = fluo.find(c => c.id === settings[key] && !used.includes(c.id)) || fallback.find(c => !used.includes(c.id));
    return [...acc, ch || null];
  }, []);
}
